import { cameraSrc, type BayCamera } from './cameras';

/**
 * Expanded camera view, over the whole window.
 *
 * The panel frame is around 340px wide, which is too small to read sea state
 * off. This scales the feed up to whatever the source can actually support and
 * no further: Caltrans publishes 320x260, so it is capped at 2x, while the
 * Crissy embeds advertise up to 1280x960 and get to use it. Upscaling past that
 * only produces a larger blur.
 */

/** How far a still may be scaled past native before it stops being worth it. */
const MAX_STILL_SCALE = 2;
/** Room left for the caption and window chrome. */
const MARGIN = { x: 64, y: 150 };

/**
 * The size the operator's player is built for.
 *
 * It is responsive, and at any other width it reflows into a broken column with
 * the picture pushed off screen — the Conservancy's own page only ever embeds it
 * at 640x480 or 320x240. So the iframe is always laid out at 640x480 and the
 * whole element is scaled with a transform, which leaves the internal layout
 * untouched.
 */
export const EMBED_LAYOUT = { width: 640, height: 480 };

/** How far a player may be scaled up before the stream turns to mush. */
const MAX_EMBED_SCALE = 1.5;

export function embedFrame(_camera: BayCamera): { width: number; height: number } {
  return { ...EMBED_LAYOUT };
}

/** The scale to display a camera at, given the space available. */
export function fitScale(camera: BayCamera): number {
  const base = camera.embedUrl ? EMBED_LAYOUT : { width: camera.native[0], height: camera.native[1] };
  const ceiling = camera.embedUrl ? MAX_EMBED_SCALE : MAX_STILL_SCALE;

  return Math.min(
    (window.innerWidth - MARGIN.x) / base.width,
    (window.innerHeight - MARGIN.y) / base.height,
    ceiling,
  );
}

export function fitted(camera: BayCamera): { width: number; height: number } {
  const base = camera.embedUrl ? EMBED_LAYOUT : { width: camera.native[0], height: camera.native[1] };
  const scale = fitScale(camera);
  return { width: Math.round(base.width * scale), height: Math.round(base.height * scale) };
}

export function openCameraLightbox(camera: BayCamera): void {
  document.querySelector('.lightbox')?.remove();

  const { width, height } = fitted(camera);

  const root = document.createElement('div');
  root.className = 'lightbox';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-label', `${camera.name}: ${camera.view}`);
  root.tabIndex = -1;

  root.innerHTML = `
    <div class="lightbox-frame" style="width:${width}px">
      <header class="lightbox-head">
        <div>
          <h2>${camera.name}</h2>
          <p>${camera.view}</p>
        </div>
        <button type="button" class="lightbox-close" aria-label="Close expanded view">×</button>
      </header>
      ${camera.embedUrl
        // Laid out at its designed size and scaled as a whole; any other width
        // makes the player reflow and lose the picture.
        ? `<div class="lightbox-embed" style="width:${width}px;height:${height}px">
             <iframe
               class="lightbox-media"
               src="${camera.embedUrl}"
               title="Live view: ${camera.view}"
               scrolling="no"
               referrerpolicy="no-referrer-when-downgrade"
               width="${EMBED_LAYOUT.width}"
               height="${EMBED_LAYOUT.height}"
               style="transform:scale(${fitScale(camera)})"
             ></iframe>
           </div>`
        : `<img class="lightbox-media" alt="Live view: ${camera.view}"
               style="width:${width}px;height:${height}px">`}
      <p class="lightbox-credit">
        ${camera.credit} · source ${camera.native[0]}×${camera.native[1]}
        ${camera.imageUrl ? '<span class="lightbox-age"></span>' : ''}
      </p>
    </div>`;

  const close = () => {
    window.clearInterval(timer);
    document.removeEventListener('keydown', onKey);
    root.remove();
  };

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation(); // Do not also close the detail panel behind it.
      close();
    }
  };

  root.querySelector<HTMLButtonElement>('.lightbox-close')!.addEventListener('click', close);
  // Clicking the backdrop dismisses; clicking the picture must not.
  root.addEventListener('click', (e) => {
    if (e.target === root) close();
  });
  document.addEventListener('keydown', onKey);

  let timer: number | undefined;
  const img = root.querySelector<HTMLImageElement>('img.lightbox-media');

  if (img) {
    const ageEl = root.querySelector<HTMLElement>('.lightbox-age')!;
    const fmt = new Intl.DateTimeFormat([], { hour: 'numeric', minute: '2-digit', second: '2-digit' });

    const load = () => {
      const at = new Date();
      const next = new Image();
      next.onload = () => {
        img.src = next.src;
        ageEl.textContent = ` · updated ${fmt.format(at)}`;
      };
      next.src = cameraSrc(camera, at);
    };
    load();
    timer = window.setInterval(load, 60_000);
  }

  document.body.append(root);
  root.focus({ preventScroll: true });
}
